import type { SmartCreationReviewTarget, SmartCreationReviewWorkspace } from "@adpilot/shared";
import {
  IconChevronDown,
  IconChevronRight,
  IconHandStop,
  IconLayersIntersect,
  IconMaximize,
  IconMinus,
  IconPlus,
  IconSpeakerphone,
  IconTargetArrow,
} from "@tabler/icons-react";
import {
  Background,
  BackgroundVariant,
  type Edge,
  Handle,
  type Node,
  type NodeChange,
  type NodeProps,
  Panel,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  useViewport,
  type XYPosition,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface StructureNodeData extends Record<string, unknown> {
  title: string;
  subtitle: string;
  detail: string;
  kind: "campaign" | "group" | "target";
  tone?: "info" | "success" | "neutral" | "phrase" | "exact";
  excluded?: boolean;
  expanded?: boolean;
  action?: string;
  toggle?: () => void;
}
type StructureNode = Node<StructureNodeData, "structure">;

function StructureCard({ data }: NodeProps<StructureNode>) {
  const { t } = useTranslation("smartCreation");
  const Icon =
    data.kind === "campaign"
      ? IconSpeakerphone
      : data.kind === "group"
        ? IconLayersIntersect
        : IconTargetArrow;
  return (
    <div
      className={cn(
        "relative rounded-[10px] bg-popover px-3 py-3 text-foreground ring-1 ring-line/40",
        data.excluded && "opacity-50",
      )}
    >
      {data.kind !== "campaign" && (
        <Handle type="target" position={Position.Left} isConnectable={false} />
      )}
      <p className="mb-1 text-[10px] text-muted-foreground">
        {t(`review.preview.graph.kind.${data.kind}`)}
      </p>
      <div className="flex items-center gap-2">
        <Icon className="size-4 shrink-0 text-muted-foreground" stroke={1.8} />
        <span
          className={cn(
            "text-[13px] font-semibold",
            data.kind === "target" ? "break-words leading-5" : "truncate",
          )}
          title={data.title}
        >
          {data.title}
        </span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <Badge
          size="compact"
          variant={
            data.tone === "phrase" || data.tone === "exact" ? "neutral" : (data.tone ?? "neutral")
          }
          className={cn(
            "rounded-md text-[11px] font-normal",
            data.tone === "phrase" &&
              "border-match-phrase-border bg-match-phrase-background text-match-phrase-foreground",
            data.tone === "exact" &&
              "border-match-exact-border bg-match-exact-background text-match-exact-foreground",
          )}
        >
          {data.subtitle}
        </Badge>
      </div>
      <p className="mt-2 truncate text-xs text-muted-foreground" title={data.detail}>
        {data.detail}
      </p>
      {data.toggle && (
        <Button
          variant="ghost"
          size="sm"
          className="nodrag nopan mt-2 w-full justify-between text-xs"
          aria-label={`${data.action} · ${data.title}`}
          aria-expanded={data.expanded}
          onClick={data.toggle}
        >
          {data.action}
          {data.expanded ? (
            <IconChevronDown className="size-3.5" />
          ) : (
            <IconChevronRight className="size-3.5" />
          )}
        </Button>
      )}
      {(data.kind === "campaign" || data.kind === "group") && (
        <Handle type="source" position={Position.Right} isConnectable={false} />
      )}
    </div>
  );
}
const nodeTypes = { structure: StructureCard };

function CanvasControls() {
  const { t } = useTranslation("smartCreation");
  const { zoomIn, zoomOut, zoomTo, fitView } = useReactFlow();
  const { zoom } = useViewport();
  return (
    <Panel
      position="bottom-left"
      className="flex items-center gap-1 rounded-lg bg-popover p-1 shadow-sm"
    >
      <Button
        variant="outline"
        size="icon-sm"
        aria-label={t("review.preview.graph.zoomOut")}
        onClick={() => void zoomOut()}
      >
        <IconMinus />
      </Button>
      <Button
        variant="outline"
        size="sm"
        aria-label={t("review.preview.graph.resetZoom")}
        onClick={() => void zoomTo(1)}
      >
        {Math.round(zoom * 100)}%
      </Button>
      <Button
        variant="outline"
        size="icon-sm"
        aria-label={t("review.preview.graph.zoomIn")}
        onClick={() => void zoomIn()}
      >
        <IconPlus />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => void fitView({ padding: 0.15, maxZoom: 1 })}
      >
        <IconMaximize />
        {t("review.preview.graph.fit")}
      </Button>
      <span
        className="grid size-8 place-items-center text-muted-foreground"
        title={t("review.preview.graph.pan")}
      >
        <IconHandStop className="size-4" />
      </span>
    </Panel>
  );
}

export function ReviewStructureGraph({ workspace }: { workspace: SmartCreationReviewWorkspace }) {
  const { t, i18n } = useTranslation("smartCreation");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [positions, setPositions] = useState<Record<string, XYPosition>>({});
  const onNodesChange = useCallback((changes: NodeChange<StructureNode>[]) => {
    const moves = changes.filter((change) => change.type === "position" && change.position);
    if (!moves.length) return;
    setPositions((previous) => {
      const next = { ...previous };
      for (const move of moves)
        if (move.type === "position" && move.position) next[move.id] = move.position;
      return next;
    });
  }, []);
  const toggle = useCallback(
    (id: string) =>
      setExpanded((previous) => {
        const next = new Set(previous);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    [],
  );
  const money = useMemo(
    () =>
      new Intl.NumberFormat(i18n.resolvedLanguage, {
        style: "currency",
        currency: workspace.summary.currencyCode,
      }),
    [i18n.resolvedLanguage, workspace.summary.currencyCode],
  );
  const { nodes, edges } = useMemo(() => {
    const nodes: StructureNode[] = [];
    const edges: Edge[] = [];
    const add = (
      id: string,
      x: number,
      y: number,
      width: number,
      data: StructureNodeData,
      parent?: string,
    ) => {
      nodes.push({
        id,
        type: "structure",
        position: { x, y },
        style: { width, pointerEvents: "auto" },
        data,
      });
      if (parent)
        edges.push({
          id: `${parent}:${id}`,
          source: parent,
          target: id,
          type: "default",
          style: {
            stroke: "var(--paper-line)",
            strokeWidth: 1.5,
          },
        });
    };
    let row = 0;
    for (const campaign of workspace.graph.campaigns) {
      const id = campaign.ref.lineageKey;
      const groups = workspace.graph.adGroups.filter((group) => group.campaignLineageKey === id);
      const targets = workspace.graph.targets.filter((target) =>
        groups.some((group) => group.ref.lineageKey === target.adGroupLineageKey),
      );
      const start = row;
      for (const group of groups) {
        const groupId = group.ref.lineageKey;
        const groupTargets = targets.filter((target) => target.adGroupLineageKey === groupId);
        const groupStart = row;
        if (expanded.has(groupId))
          for (const target of groupTargets) {
            add(
              target.ref.lineageKey,
              560,
              row,
              180,
              {
                title:
                  target.type === "automatic"
                    ? t(`review.preview.graph.expressions.${target.expressionType}`)
                    : targetLabel(target),
                tone:
                  target.type === "keyword"
                    ? target.matchType.effective === "broad"
                      ? "info"
                      : target.matchType.effective
                    : target.type === "product"
                      ? target.matchType === "expanded"
                        ? "info"
                        : "exact"
                      : "success",
                kind: "target",
                subtitle:
                  target.type === "keyword"
                    ? t(`review.match.${target.matchType.effective}`)
                    : t(`review.targetType.${target.type}`),
                detail: money.format(target.bid.effective),
                excluded: campaign.excluded.effective || target.excluded.effective,
              },
              groupId,
            );
            row += 120 + Math.max(0, Math.ceil(targetLabel(target).length / 19) - 1) * 20;
          }
        row = Math.max(row, groupStart + 160);
        add(
          groupId,
          280,
          groupStart,
          240,
          {
            title: group.name.effective,
            kind: "group",
            tone:
              groupTargets[0]?.type === "keyword"
                ? groupTargets[0].matchType.effective === "broad"
                  ? "info"
                  : groupTargets[0].matchType.effective
                : "neutral",
            subtitle:
              groupTargets[0]?.type === "keyword"
                ? t(`review.match.${groupTargets[0].matchType.effective}`)
                : t(`review.targetType.${groupTargets[0]?.type ?? "product"}`),
            detail: `${t("review.preview.groupTargetCount", { count: groupTargets.length })} · ${group.products.map((product) => product.asin).join(" · ")}`,
            expanded: expanded.has(groupId),
            action: t(
              expanded.has(groupId)
                ? "review.preview.graph.hideTargets"
                : "review.preview.graph.showTargets",
            ),
            excluded: campaign.excluded.effective,
            toggle: () => toggle(groupId),
          },
          id,
        );
      }
      row = Math.max(row, start + 160);
      add(id, 0, start, 220, {
        title: campaign.name.effective,
        kind: "campaign",
        tone: targets.some((target) => target.type === "automatic") ? "info" : "success",
        subtitle: t(
          targets.some((target) => target.type === "automatic")
            ? "preview.automatic"
            : "preview.manual",
        ),
        detail: t("review.preview.graph.counts", {
          groups: groups.length,
          targets: targets.length,
        }),
        excluded: campaign.excluded.effective,
      });
      row += 32;
    }
    return {
      nodes: nodes.map((node) => ({ ...node, position: positions[node.id] ?? node.position })),
      edges,
    };
  }, [workspace, expanded, t, money, toggle, positions]);
  return (
    <div>
      <p className="mx-6 mb-3 text-xs text-muted-foreground">{t("review.preview.graph.hint")}</p>
      <div
        data-testid="review-structure-graph"
        className="review-structure-graph mx-6 mb-6 h-[600px] min-h-64 overflow-hidden rounded-[10px] border border-line bg-canvas"
        style={{ height: "min(600px, calc(85dvh - 210px))" }}
      >
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            nodesDraggable
            onNodesChange={onNodesChange}
            nodesConnectable={false}
            edgesFocusable={false}
            deleteKeyCode={null}
            fitView
            fitViewOptions={{
              padding: 0.15,
              maxZoom: 1,
              nodes: nodes
                .filter(
                  (node) =>
                    node.id === workspace.graph.campaigns[0]?.ref.lineageKey ||
                    edges.some(
                      (edge) =>
                        edge.source === workspace.graph.campaigns[0]?.ref.lineageKey &&
                        edge.target === node.id,
                    ),
                )
                .sort(
                  (a, b) => Number(b.data.kind === "campaign") - Number(a.data.kind === "campaign"),
                )
                .slice(0, 4),
            }}
            minZoom={0.1}
            maxZoom={2}
            elementsSelectable={false}
            nodesFocusable={false}
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={20}
              size={1.8}
              color="color-mix(in srgb, var(--paper-muted) 24%, transparent)"
            />
            <CanvasControls />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
    </div>
  );
}

function targetLabel(target: SmartCreationReviewTarget): string {
  if (target.type === "keyword") return target.keyword;
  if (target.type === "product") return target.asin;
  if (target.type === "automatic") return target.expressionType;
  return target.type === "category_brand"
    ? `${target.categoryId} · ${target.brandId}`
    : target.categoryId;
}
